import { useQuery } from '@tanstack/react-query'
import { MONTH_MS } from 'data/constants'
import { MiniappEmbed, MiniappEmbedSchema } from 'use-towns-client'
import { env, isTownBanned } from 'utils'
import { getTownParamsFromUrl } from 'utils/getTownParamsFromUrl'
import { z } from 'zod'

import { axiosClient } from '../apiClient'

const baseUnfurlSchema = z.object({
    url: z.string(),
    title: z.string().optional(),
    description: z.string().optional(),
    image: z
        .object({
            width: z.number().optional(),
            height: z.number().optional(),
            url: z.string(),
        })
        .optional(),
})

const miniappUnfurlSchema = baseUnfurlSchema.extend({
    type: z.literal('miniapp'),
    info: MiniappEmbedSchema,
})

const imageUnfurlSchema = baseUnfurlSchema.extend({
    type: z.literal('image').optional(),
})

const genericUnfurlSchema = baseUnfurlSchema.extend({
    type: z.literal('generic').optional(),
})

// Put miniapp first so it's checked before generic (which has optional type)
const unfurledLinkSchema = z.union([miniappUnfurlSchema, imageUnfurlSchema, genericUnfurlSchema])

const unfurledLinkResponseSchema = z.object({
    data: z.array(unfurledLinkSchema),
})

export type UnfurledLink = z.infer<typeof unfurledLinkSchema>
export type { MiniappEmbed }

export async function getUnfurlContent(urlsArray: string[]) {
    const UNFURL_SERVER_URL = env.VITE_UNFURL_SERVER_URL

    // Filter out URLs for banned towns
    const filteredUrls = urlsArray.filter((url) => {
        const townData = getTownParamsFromUrl(url)
        if (townData?.townId && isTownBanned(townData.townId)) {
            return false
        }
        return true
    })

    if (filteredUrls.length === 0) {
        return { data: [] }
    }

    const encodedUrls = filteredUrls.map((url) => `&url=${encodeURIComponent(url)}`)
    const requestUrl = `${UNFURL_SERVER_URL}?${encodedUrls.join('')}`

    try {
        const response = await axiosClient.get(requestUrl)

        const parsed = unfurledLinkResponseSchema.safeParse(response)

        if (parsed.success) {
            return parsed.data
        } else if (parsed.error) {
            console.error('[Unfurl] Error parsing unfurled link response:', parsed.error)
            console.error('[Unfurl] Raw response that failed parsing:', response)
        }
    } catch (error) {
        console.error('[Unfurl] Failed to fetch unfurl content:', error)
        if (error instanceof Error && error.message.includes('CORS')) {
            console.error('[Unfurl] CORS error detected - check origin matching in unfurl worker')
        }
        throw error
    }
}

export function useUnfurlContent({
    urlsArray,
    enabled,
}: {
    urlsArray: string[]
    enabled: boolean
}) {
    // using urlsArray as query key, maybe need to add event id too?
    // eslint-disable-next-line no-restricted-syntax
    const ret = useQuery({
        queryKey: urlsArray,
        queryFn: () => {
            return getUnfurlContent(urlsArray)
        },
        select: (response) => {
            const data = response?.data
            if (data && Array.isArray(data)) {
                return data
            }
            return data
        },
        enabled,
        // unfurl content doesn't need to be refetched
        // if user edits their message, that's fine, it's a new query key so will be fetched
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        staleTime: MONTH_MS,
        gcTime: MONTH_MS,
    })

    return ret
}
